import { AvField, AvForm } from "availity-reactstrap-validation"
import { getCode } from "helpers/basic_helper"
import { get } from "helpers/api_helper"
import { useEffect, useState } from "react"
import DatePicker from "react-datepicker"
import Drawer from "react-modern-drawer"
import "react-modern-drawer/dist/index.css"
import { MultiSelect } from "react-multi-select-component"
import { GetHelper } from "../../Masters/CallHelper"

const parseSafeDate = d => {
  if (!d || d === "0000-00-00" || d === "0000-00-00 00:00:00" || d === "null" || d === "") return null
  if (d instanceof Date) {
    return isNaN(d.getTime()) ? null : d
  }
  const parsed = new Date(d)
  return isNaN(parsed.getTime()) ? null : parsed
}

const parseMultiSelectValues = (val, options = []) => {
  if (!val) return []
  let codes = []
  if (Array.isArray(val)) {
    codes = val.map(item => (typeof item === "object" && item !== null ? item.value : item))
  } else if (typeof val === "string") {
    codes = val.split(",").map(s => s.trim()).filter(Boolean)
  } else {
    codes = [String(val)]
  }
  const stringCodes = codes.map(String)
  const matched = (options || []).filter(opt => stringCodes.includes(String(opt.value)))
  const matchedCodes = matched.map(m => String(m.value))
  const unmatched = stringCodes
    .filter(c => !matchedCodes.includes(c))
    .map(c => ({ label: c, value: c }))
  return [...matched, ...unmatched]
}

const Form = ({
  Position = "right",
  isOpen,
  onDrawerClose,
  title,
  width = 50,
  submitForm,
  editableObj,
  DDMasters = {},
  length = 0,
}) => {
  const getInitialData = obj => ({
    id: obj?.id || "",
    firstname: obj?.firstname || "",
    middlename: obj?.middlename || "",
    lastname: obj?.lastname || "",
    gender: obj?.gender !== undefined && obj?.gender !== null ? String(obj.gender) : "1",
    code: obj?.code || getCode("employee", length),
    image: obj?.image || "",
    mobile: obj?.mobile || "",
    phone: obj?.phone || "",
    dob: parseSafeDate(obj?.dob),
    doj: parseSafeDate(obj?.doj),
    email: obj?.email || "",
    role: obj?.role || "",
    shift: obj?.shift || "",
    shiftgroup: obj?.shiftgroup || "",
    designation: obj?.designation || "",
    department: obj?.department || "",
    lab_department: obj?.lab_department || "",
    location: obj?.location || "",
    pan: obj?.pan || "",
    uid: obj?.uid || "",
    address1: obj?.address1 || "",
    address2: obj?.address2 || "",
    branch: obj?.branch_id || obj?.branch || "",
  })

  const [data, setData] = useState(() => getInitialData(editableObj))
  const [roles, setRoles] = useState([])
  const [localMasters, setLocalMasters] = useState(DDMasters || {})

  useEffect(() => {
    setData(getInitialData(editableObj))
  }, [editableObj])

  useEffect(() => {
    if (DDMasters && (DDMasters.branches || DDMasters.department)) {
      setLocalMasters(DDMasters)
    } else {
      get("/api/employee/getOptions").then(opts => {
        if (opts && (opts.status === 200 || opts.response)) {
          setLocalMasters(opts.response || opts)
        }
      })
    }
  }, [DDMasters])

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const getRoles = await GetHelper(`${process.env.REACT_APP_ROLE || "m13"}`)
        let roleList = []
        if (Array.isArray(getRoles)) {
          roleList = getRoles
        } else if (Array.isArray(getRoles?.rows)) {
          roleList = getRoles.rows
        } else if (Array.isArray(getRoles?.response)) {
          roleList = getRoles.response
        }
        setRoles(
          roleList.map(item => ({
            value: item.id || item.code,
            label: item.title,
          }))
        )
      } catch (e) {
        console.error("Fetch roles error:", e)
      }
    }
    fetchRoles()
  }, [])

  // FormFields
  const fields = [
    {
      name: "branch",
      label: "Branch",
      placeholder: "Please Select Branch",
      required: true,
      type: "select",
      options: (localMasters?.branches || []).map(v => ({
        label: v.title || v.branch,
        value: v.value ?? v.id,
      })),
      col: 12,
    },
    {
      name: "firstname",
      label: "First Name",
      placeholder: "Please Enter First Name",
      required: true,
      type: "text",
      col: 4,
    },
    {
      name: "middlename",
      label: "Middle Name",
      placeholder: "Please Enter Middle Name",
      required: false,
      type: "text",
      col: 4,
    },
    {
      name: "lastname",
      label: "Last Name",
      placeholder: "Please Enter Last Name",
      required: true,
      type: "text",
      col: 4,
    },
    {
      name: "gender",
      label: "Gender",
      placeholder: "Please Select Gender",
      required: true,
      type: "select",
      col: 4,
      options: [
        { value: "1", label: "Male" },
        { value: "2", label: "Female" },
      ],
    },
    {
      name: "dob",
      label: "Date of Birth",
      placeholder: "Please Select Date of Birth",
      required: false,
      type: "date",
      col: 4,
    },
    {
      name: "code",
      label: "Employee Code",
      placeholder: "Please Enter Employee Code",
      required: true,
      type: "text",
      col: 4,
    },
    {
      name: "mobile",
      label: "Mobile Number",
      placeholder: "Please Enter Mobile Number",
      required: true,
      type: "number",
      col: 4,
    },
    {
      name: "phone",
      label: "Phone Number",
      placeholder: "Please Enter Phone Number",
      required: false,
      type: "number",
      col: 4,
    },
    {
      name: "email",
      label: "Email Address",
      placeholder: "Please Enter Email Address",
      required: true,
      type: "text",
      col: 4,
    },
    {
      name: "doj",
      label: "Date of Join",
      placeholder: "Please Select Date of Join",
      required: false,
      type: "date",
      col: 6,
    },
    {
      name: "role",
      label: "Employee Role",
      placeholder: "Please Select Employee Role",
      required: true,
      type: "select",
      col: 6,
      options: roles.filter(v => v.label !== "Superadmin"),
    },
    {
      name: "department",
      label: "Department",
      placeholder: "Please Select Employee Department",
      required: true,
      type: "select",
      col: 6,
      options: (localMasters?.department || []).map(v => ({
        label: v.title,
        value: v.code ?? v.id,
      })),
    },
    {
      name: "designation",
      label: "Designation",
      placeholder: "Please Select Employee Designation",
      required: true,
      type: "select",
      col: 6,
      options: (localMasters?.designation || []).map(v => ({
        label: v.title,
        value: v.code ?? v.id,
      })),
    },
    {
      name: "lab_department",
      label: "Lab Department",
      placeholder: "Please Select Lab Department",
      required: false,
      type: "multiselect",
      col: 6,
      options: (localMasters?.lab_department || []).map(v => ({
        label: v.title,
        value: String(v.code ?? v.id),
      })),
    },
    {
      name: "pan",
      label: "Pan Number",
      placeholder: "Please Enter Pan Number",
      required: false,
      type: "text",
      col: 6,
    },
    {
      name: "uid",
      label: "UID",
      placeholder: "Please Enter UID",
      required: false,
      type: "text",
      col: 6,
    },
    {
      name: "address1",
      label: "Current Address",
      placeholder: "Please Enter Current Address",
      required: false,
      type: "textarea",
      col: 6,
    },
    {
      name: "address2",
      label: "Permanent Address",
      placeholder: "Please Enter Permanent Address",
      required: false,
      type: "textarea",
      col: 6,
    },
  ]

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768
  const numericWidth = typeof width === "number" ? width : parseInt(width, 10) || 55
  const computedWidth = isMobile ? "100%" : `${numericWidth}%`
  const minWidth = isMobile ? "100%" : "600px"
  const maxWidth = isMobile ? "100%" : "1050px"

  return (
    <Drawer
      open={isOpen}
      onClose={onDrawerClose}
      direction={Position}
      className="masterForm"
      style={{
        width: computedWidth,
        minWidth: minWidth,
        maxWidth: maxWidth,
        boxShadow: "-4px 0 25px rgba(0,0,0,0.15)",
        background: "#ffffff",
      }}
    >
      <div className="d-flex flex-column h-100">
        <div
          className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom"
          style={{ background: "#f8fafc" }}
        >
          <h5 className="m-0 text-dark fw-bold font-size-16 d-flex align-items-center">
            <i className="mdi mdi-account-cog text-primary me-2 font-size-20"></i>
            {title}
          </h5>
          <button
            type="button"
            className="btn btn-sm btn-light rounded-circle"
            style={{ width: "32px", height: "32px", padding: 0 }}
            onClick={onDrawerClose}
            title="Close"
          >
            <i className="mdi mdi-close font-size-18 text-muted"></i>
          </button>
        </div>
        <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
          <AvForm
            className="form-horizontal"
            model={data}
            onValidSubmit={() => {
              submitForm(data)
            }}
          >
            <input type="hidden" name="id" value={data.id || ""} />
            <div className="row">
              <div className="col-md-12 mb-3">
                <div className="row">
                  {fields.map((v, i) => {
                    const fieldValue =
                      data[v.name] !== undefined && data[v.name] !== null
                        ? data[v.name]
                        : ""

                    if (v.type === "multiselect") {
                      const selectedValues = parseMultiSelectValues(fieldValue, v.options || [])
                      return (
                        <div
                          className={`col-md-${v.col} mb-3`}
                          key={`fld_${v.name}_${i}`}
                        >
                          <label className="form-label">{v.label}</label>
                          <MultiSelect
                            options={v.options || []}
                            value={selectedValues}
                            onChange={selected => {
                              const codes = (selected || []).map(item => item.value)
                              setData(c => ({
                                ...c,
                                [v.name]: codes.join(","),
                              }))
                            }}
                            labelledBy={v.placeholder || `Select ${v.label}`}
                            hasSelectAll={true}
                            disableSearch={false}
                            overrideStrings={{
                              selectSomeItems: v.placeholder || "Select...",
                              allItemsAreSelected: "All selected",
                              selectAll: "Select All",
                              search: "Search...",
                            }}
                          />
                        </div>
                      )
                    }

                    if (v.type === "select") {
                      return (
                        <div
                          className={`col-md-${v.col} mb-3`}
                          key={`fld_${v.name}_${i}`}
                        >
                          <label className="form-label">{v.label}</label>
                          <select
                            className={`form-control ${v.class || ""}`}
                            name={v.name}
                            value={String(fieldValue)}
                            onChange={e =>
                              setData(c => ({
                                ...c,
                                [v.name]: e.target.value,
                              }))
                            }
                            required={v.required}
                          >
                            <option value=""> --- </option>
                            {v.options?.map((vp, ip) => (
                              <option
                                value={String(vp.value)}
                                key={`opt_${v.name}_${vp.value}_${ip}`}
                              >
                                {vp.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )
                    }

                    if (v.type === "date") {
                      return (
                        <div
                          className={`col-md-${v.col} mb-3`}
                          key={`fld_${v.name}_${i}`}
                        >
                          <label className="form-label">{v.label}</label>
                          <DatePicker
                            className={`form-control ${v.class || ""}`}
                            selected={parseSafeDate(fieldValue)}
                            onChange={x => {
                              const validDate = parseSafeDate(x)
                              setData(c => ({ ...c, [v.name]: validDate }))
                            }}
                            dateFormat="yyyy-MM-dd"
                            isClearable
                          />
                        </div>
                      )
                    }

                    return (
                      <div
                        className={`col-md-${v.col} mb-3`}
                        key={`fld_${v.name}_${i}`}
                      >
                        <AvField
                          key={`${v.name}_input`}
                          name={v.name}
                          label={v.label}
                          className={`form-control ${v.class || ""}`}
                          placeholder={v.placeholder}
                          type={v.type}
                          value={fieldValue}
                          onChange={e =>
                            setData(c => ({
                              ...c,
                              [v.name]: e.target.value,
                            }))
                          }
                          required={v.required}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="mt-3 d-grid">
              <button
                className="btn btn-primary btn-block waves-effect waves-light"
                type="submit"
              >
                Submit
              </button>
            </div>
          </AvForm>
        </div>
      </div>
    </Drawer>
  )
}

export default Form
